import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import VendorOnboardingWizard from "@/components/VendorOnboardingWizard";
import { money, fmtDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Warning, Key, Sliders, MagnifyingGlass, X, Trash } from "@phosphor-icons/react";
import { devWarn } from "@/lib/logger";
import AffiliateManageModal from "@/components/AffiliateManageModal";
import OrgDeleteDialog from "@/components/OrgDeleteDialog";
import { LoginStatusBadge } from "@/components/buyer/LoginStatusBadge";
import { HoursBadge } from "@/components/buyer/HoursBadge";
import InviteBuyerModal from "@/components/delivery/InviteBuyerModal";
import { SetupProgressList } from "@/components/buyersetup/SetupProgressList";
import { BuyerLoginFixes } from "@/components/buyer/BuyerLoginFixes";

const BLANK_FORM = {
  name: "", contact_email: "", contact_phone: "",
  price: "", return_window_days: 5, replacement_cap: "", notes: "",
  duplicate_policy: "moderate",
  cap_daily: "", cap_weekly: "", cap_monthly: "",
};

// Iter-61.4 defaults keep the pre-existing "Name A→Z" experience.
const DEFAULT_SORT = "name_asc";
const DEFAULT_STATUS = "all";
const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "name_asc",     label: "Name A → Z" },
  { value: "name_desc",    label: "Name Z → A" },
  { value: "created_desc", label: "Newest Created" },
  { value: "created_asc",  label: "Oldest Created" },
];

export default function Organizations({ type }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [showWizard, setShowWizard] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, has_next: false });
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [capStatuses, setCapStatuses] = useState({});
  const [logins, setLogins] = useState({});
  const [inviteTo, setInviteTo] = useState({});
  const [inviting, setInviting] = useState(null);
  const [manageOrg, setManageOrg] = useState(null);
  // Iter-73: super_admin-only cascade delete confirm modal.
  const [deleteOrg, setDeleteOrg] = useState(null);

  // Iter-61.4 — Search / Sort / Status / Pagination.
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [hours, setHours] = useState("all");
  const [page, setPage] = useState(1);
  const [recentlyCreated, setRecentlyCreated] = useState(null); // { id, name }
  const navigate = useNavigate();

  const filtersActive = q !== "" || sort !== DEFAULT_SORT || status !== DEFAULT_STATUS || hours !== "all";

  const load = useCallback(async (opts = {}) => {
    const params = new URLSearchParams();
    params.set("type", type);
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    if (type === "client" && hours !== "all") params.set("hours", hours);
    params.set("sort", sort);
    params.set("page", String(opts.page ?? page));
    params.set("page_size", String(PAGE_SIZE));
    const { data } = await api.get(`/organizations?${params.toString()}`);
    setRows(data.organizations || []);
    if (data.pagination) setPagination(data.pagination);
    if (type === "client") {
      api.get("/admin/buyer-login-status").then((r) => { setLogins(r.data.clients || {}); setInviteTo(r.data.invite_to || {}); }).catch(() => setLogins(null));
      const withCaps = (data.organizations || []).filter((o) => o.caps && Object.keys(o.caps).length);
      const statuses = {};
      await Promise.all(withCaps.map(async (o) => {
        try {
          const r = await api.get(`/organizations/${o.id}/cap-status`);
          statuses[o.id] = r.data;
        } catch (err) {
          devWarn(`cap-status fetch failed for org ${o.id}:`, err);
        }
      }));
      setCapStatuses(statuses);
    }
  }, [type, q, sort, status, hours, page]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [type, q, sort, status, hours]);
  useEffect(() => { load(); }, [load]);

  // Debounce search input → q (300ms).
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(qInput.trim()), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [qInput]);

  const clearFilters = () => {
    setQInput("");
    setQ("");
    setSort(DEFAULT_SORT);
    setStatus(DEFAULT_STATUS);
    setHours("all");
    setPage(1);
    setRecentlyCreated(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        type, name: form.name,
        contact_email: form.contact_email, contact_phone: form.contact_phone,
        notes: form.notes,
      };
      if (type === "client") {
        // Duplicate policy is only enforced against the matched buyer
        // in `lead_routes._apply_duplicate_check`, so we only send it
        // (and only show its picker below) for client orgs. Affiliate
        // orgs get the backend default ("moderate"), which is unused.
        body.duplicate_policy = form.duplicate_policy;
        body.price = form.price === "" ? null : Number(form.price);
        body.return_window_days = form.return_window_days === "" ? null : Number(form.return_window_days);
        body.replacement_cap = form.replacement_cap === "" ? null : Number(form.replacement_cap);
        const caps = {};
        if (form.cap_daily)   caps.daily   = Number(form.cap_daily);
        if (form.cap_weekly)  caps.weekly  = Number(form.cap_weekly);
        if (form.cap_monthly) caps.monthly = Number(form.cap_monthly);
        body.caps = Object.keys(caps).length ? caps : null;
      }
      const { data: created } = await api.post("/organizations", body);
      if (type === "client") {
        // Iter-73.5 — deep-link toast so admins go straight to the
        // criteria form after creating the buyer.
        toast.success(`Client "${created.name}" created`, {
          duration: 8000,
          action: {
            label: "Set criteria →",
            onClick: () => navigate(`/clients/${created.id}/manage`),
          },
        });
      } else {
        toast.success(`${type} created`);
      }
      setShowNew(false);
      setForm(BLANK_FORM);
      setRecentlyCreated({ id: created.id, name: created.name });
      // Re-load current list; if the new record isn't visible we render a
      // one-click banner offering to clear filters.
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const pause = async (id) => { await api.post(`/organizations/${id}/pause`); toast.success("Paused"); load(); };
  const resume = async (id) => { await api.post(`/organizations/${id}/resume`); toast.success("Resumed"); load(); };

  const title = type === "client" ? "Clients" : "Affiliates";
  const total = pagination.total || 0;
  const visible = rows.length;
  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const end   = (pagination.page - 1) * pagination.page_size + visible;

  // New-record visibility check — is the just-created record on this page?
  const createdVisible = useMemo(() => {
    if (!recentlyCreated) return true;
    return rows.some((r) => r.id === recentlyCreated.id);
  }, [rows, recentlyCreated]);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="label-mono">Directory</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">{title}</h1>
        </div>
        <div className="self-start md:self-auto inline-flex flex-wrap gap-2">
          {type === "affiliate" && isSuperAdmin && (
            <button onClick={() => setShowWizard(true)} data-testid="open-vendor-wizard"
              className="bg-emerald-700 text-white px-3 py-2 text-xs uppercase tracking-widest border border-emerald-800 hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
              🚀 Affiliate wizard
            </button>
          )}
          {type === "client" && (
            <button onClick={() => navigate("/clients/setup")} data-testid="open-buyer-setup"
              className="bg-emerald-700 text-white px-3 py-2 text-xs uppercase tracking-widest border border-emerald-800 hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
              Set up a buyer
            </button>
          )}
          <button onClick={() => setShowNew((v) => !v)} data-testid={`toggle-new-${type}`}
            className="bg-black text-white px-3 py-2 text-xs uppercase tracking-widest border border-black hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
            {showNew ? "Close" : `New ${type}`}
          </button>
        </div>
      </div>

      {type === "client" && <BuyerLoginFixes />}
      {type === "client" && <SetupProgressList />}

      {/* Iter-61.4 — Search / Sort / Status toolbar. */}
      <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-3"
           data-testid={`${type}s-toolbar`}>
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search name, email, or phone"
            className="w-full border border-black pl-7 pr-2 py-1.5 text-sm"
            data-testid={`${type}s-search-input`}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid={`${type}s-status-filter`}
            className="border border-black px-2 py-1.5 text-sm">
            <option value="all">Status: All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
          {type === "client" && (
            <select value={hours} onChange={(e) => setHours(e.target.value)}
              data-testid="clients-hours-filter" title="Buyers taking leads right now, by their lead hours"
              className="border border-black px-2 py-1.5 text-sm">
              <option value="all">Hours: All</option>
              <option value="open">Open now</option>
              <option value="closed">Closed now</option>
            </select>
          )}
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            data-testid={`${type}s-sort-select`}
            className="border border-black px-2 py-1.5 text-sm">
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {filtersActive && (
            <button onClick={clearFilters} data-testid={`${type}s-clear-filters`}
              className="border border-black px-2 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
      </div>

      {/* New-record hidden-by-filter banner. */}
      {recentlyCreated && !createdVisible && (
        <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2 border-l-4 border-l-amber-500 bg-amber-50"
             data-testid="new-record-hidden-banner">
          <div className="text-sm flex-1">
            <b>{recentlyCreated.name}</b> was created but is hidden by your current search or filters.
          </div>
          <div className="flex gap-2">
            <button onClick={clearFilters} data-testid="banner-clear-filters"
              className="border border-black px-2 py-1 text-[11px] uppercase tracking-widest bg-black text-white hover:bg-zinc-800">
              Clear filters
            </button>
            {type === "client" && (
              <button onClick={() => navigate(`/clients/${recentlyCreated.id}/manage`)}
                data-testid="banner-view-record"
                className="border border-sky-700 text-sky-800 px-2 py-1 text-[11px] uppercase tracking-widest hover:bg-sky-700 hover:text-white">
                View client
              </button>
            )}
            <button onClick={() => setRecentlyCreated(null)} data-testid="banner-dismiss"
              className="border border-zinc-500 text-zinc-600 px-2 py-1 text-[11px] uppercase tracking-widest hover:bg-zinc-200">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={submit} className="brut-card p-5 mb-4 space-y-3" data-testid={`new-${type}-form`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" required v={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId={`new-${type}-name`} />
            <Field label="Contact email" type="email" v={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} testId={`new-${type}-email`} />
            <Field label="Contact phone" v={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} testId={`new-${type}-phone`} />
            {type === "client" && (
              <div>
                <label className="label-mono">Duplicate policy</label>
                <select value={form.duplicate_policy}
                  onChange={(e) => setForm({ ...form, duplicate_policy: e.target.value })}
                  data-testid={`new-${type}-dedupe`}
                  className="w-full border border-black px-2 py-1.5 mt-1">
                  <option value="strict">Strict (phone + email only)</option>
                  <option value="moderate">Moderate (also last name + zip)</option>
                  <option value="aggressive">Aggressive (also soundex + first-initial + city)</option>
                </select>
              </div>
            )}
            {type === "client" && (
              <>
                <Field label="Price ($/lead)" type="number" step="0.01" v={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} testId={`new-${type}-price`} />
                <Field label="Return window (days)" type="number" v={form.return_window_days} onChange={(e) => setForm({ ...form, return_window_days: e.target.value })} testId={`new-${type}-return-window`} />
                <Field label="Replacement cap" type="number" v={form.replacement_cap} onChange={(e) => setForm({ ...form, replacement_cap: e.target.value })} />
              </>
            )}
          </div>

          {type === "client" && (
            <div className="border border-dashed border-zinc-400 p-3">
              <div className="label-mono mb-1">Volume caps (leave blank for unlimited)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Daily" type="number" min={0} v={form.cap_daily}
                  onChange={(e) => setForm({ ...form, cap_daily: e.target.value })}
                  testId={`new-${type}-cap-daily`} />
                <Field label="Weekly" type="number" min={0} v={form.cap_weekly}
                  onChange={(e) => setForm({ ...form, cap_weekly: e.target.value })}
                  testId={`new-${type}-cap-weekly`} />
                <Field label="Monthly" type="number" min={0} v={form.cap_monthly}
                  onChange={(e) => setForm({ ...form, cap_monthly: e.target.value })}
                  testId={`new-${type}-cap-monthly`} />
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                Leads that would push the client past any cap are marked <b>capped</b> and skipped. They still get intake-validated and appear in the queue.
              </div>
            </div>
          )}

          <div>
            <label className="label-mono">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-black px-3 py-2 mt-1" />
          </div>
          <div className="flex justify-end">
            <button type="submit" data-testid={`submit-new-${type}`}
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">Create</button>
          </div>
        </form>
      )}

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[820px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-4 text-left">Name</th>
              <th className="py-2 px-4 text-left">Contact</th>
              {type === "affiliate" && <th className="py-2 px-4 text-left">Public Source ID</th>}
              {type === "affiliate" && <th className="py-2 px-4 text-left">API Key</th>}
              {type === "client" && <th className="py-2 px-4 text-left">Price</th>}
              {type === "client" && <th className="py-2 px-4 text-left">Return window</th>}
              {type === "client" && <th className="py-2 px-4 text-left">Caps (D/W/M)</th>}
              <th className="py-2 px-4 text-left">Status</th>
              <th className="py-2 px-4 text-left">Created</th>
              <th className="py-2 px-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => {
              const cs = capStatuses[o.id];
              return (
                <tr key={o.id} className="border-b border-zinc-200 hover:bg-zinc-100" data-testid={`org-row-${o.id}`}>
                  <td className="py-2 px-4 font-semibold">
                    {o.name}
                    {type === "client" && logins && (
                      <div className="mt-0.5"><LoginStatusBadge login={logins[o.id] || { status: "none", emails: [] }} testId={`org-login-status-${o.id}`}
                                                                onInvite={inviteTo[o.id] ? () => setInviting({ org: o, dest: inviteTo[o.id] }) : undefined} /></div>
                    )}
                    {type === "client" && o.hours_now && (
                      <div className="mt-0.5"><HoursBadge orgId={o.id} hours={o.hours_now} /></div>
                    )}
                  </td>
                  <td className="py-2 px-4">{o.contact_email || "—"}<br /><span className="text-zinc-500">{o.contact_phone || ""}</span></td>
                  {type === "affiliate" && <td className="py-2 px-4">{o.public_source_id}</td>}
                  {type === "affiliate" && <td className="py-2 px-4 text-[11px] truncate max-w-[220px]">{o.api_key || "hidden"}</td>}
                  {type === "client" && <td className="py-2 px-4">{money(o.price)}</td>}
                  {type === "client" && <td className="py-2 px-4">{o.return_window_days || 5}d</td>}
                  {type === "client" && (
                    <td className="py-2 px-4 text-[11px]" data-testid={`org-caps-${o.id}`}>
                      {!o.caps || Object.keys(o.caps).length === 0 ? (
                        <span className="text-zinc-400">—</span>
                      ) : (
                        <div className="space-y-0.5">
                          {["daily", "weekly", "monthly"].map((k) => {
                            if (!o.caps[k]) return null;
                            const isHit = cs?.would_cap?.[k];
                            return (
                              <div key={k} className={isHit ? "text-red-700 font-bold" : ""}>
                                {isHit && <Warning size={10} weight="fill" className="inline mr-0.5" />}
                                {k[0].toUpperCase()}: {cs?.current?.[k] ?? "?"} / {o.caps[k]}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="py-2 px-4"><StatusBadge status={o.status} /></td>
                  <td className="py-2 px-4 text-[11px]">{fmtDate(o.created_at)}</td>
                  <td className="py-2 px-4">
                    {type === "client" && (
                      <button
                        onClick={() => navigate(`/clients/${o.id}/manage`)}
                        data-testid={`manage-client-${o.id}`}
                        className="border border-sky-700 text-sky-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-sky-700 hover:text-white mr-1 inline-flex items-center gap-1"
                        title="Configure intake criteria, states, case types, and eligibility rules"
                      >
                        <Sliders size={10} weight="bold" /> Manage
                      </button>
                    )}
                    {type === "affiliate" && (
                      <button
                        onClick={() => setManageOrg(o)}
                        data-testid={`manage-affiliate-${o.id}`}
                        className="border border-sky-700 text-sky-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-sky-700 hover:text-white mr-1 inline-flex items-center gap-1"
                        title="Reveal / rotate api keys · Invite affiliate logins"
                      >
                        <Key size={10} weight="bold" /> Manage
                      </button>
                    )}
                    {o.status === "active" ? (
                      <button onClick={() => pause(o.id)} data-testid={`pause-${o.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">Pause</button>
                    ) : (
                      <button onClick={() => resume(o.id)} data-testid={`resume-${o.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">Resume</button>
                    )}
                    {/* Iter-73: super_admin cascade delete. Iter-73.8 —
                        also hide the button entirely for non-super_admins
                        so the UI matches the backend rule (used to fire
                        an ugly 403 toast). */}
                    {isSuperAdmin && (
                      <button
                        onClick={() => setDeleteOrg(o)}
                        data-testid={`delete-${o.id}`}
                        className="ml-1 border border-rose-700 text-rose-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-rose-700 hover:text-white inline-flex items-center gap-1"
                        title="Delete this org and every row scoped to it (super_admin only)"
                      >
                        <Trash size={10} weight="bold" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={10} className="py-6 text-center label-mono">No {title.toLowerCase()}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Result count + pagination */}
      <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs font-mono uppercase tracking-widest text-zinc-700">
        <div data-testid={`${type}s-result-count`}>
          {total === 0
            ? `No ${title.toLowerCase()} match`
            : `Showing ${start}${start !== end ? `–${end}` : ""} of ${total} ${title.toLowerCase()}`}
        </div>
        <div className="flex items-center gap-2" data-testid={`${type}s-pagination`}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            data-testid={`${type}s-page-prev`}
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Prev</button>
          <span>Page {pagination.page || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={!pagination.has_next}
            data-testid={`${type}s-page-next`}
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Next</button>
        </div>
      </div>

      {manageOrg && (
        <AffiliateManageModal
          affiliate={manageOrg}
          onClose={() => { setManageOrg(null); load(); }}
        />
      )}

      {/* Iter-73: cascade delete confirm modal — super_admin only, backend enforced. */}
      {inviting && (
        <InviteBuyerModal destination={{ id: inviting.dest.id, name: `${inviting.org.name} · ${inviting.dest.name || "their delivery"}` }} linkCompany
                          onClose={() => { setInviting(null); load(); }} />
      )}
      {deleteOrg && (
        <OrgDeleteDialog
          org={deleteOrg}
          onClose={() => setDeleteOrg(null)}
          onDeleted={() => { setDeleteOrg(null); load(); }}
        />
      )}

      {/* Iter-73.8: vendor onboarding wizard — /affiliates only, super_admin only. */}
      {showWizard && (
        <VendorOnboardingWizard
          open={showWizard}
          onClose={() => setShowWizard(false)}
          onFinished={() => { setShowWizard(false); load(); }}
        />
      )}
    </div>
  );
}

function Field({ label, v, onChange, type = "text", required = false, testId, step, min }) {
  return (
    <div>
      <label className="label-mono">{label}{required && "*"}</label>
      <input type={type} step={step} min={min} required={required} value={v} onChange={onChange}
        className="w-full border border-black px-3 py-2 mt-1" data-testid={testId} />
    </div>
  );
}
